/**
 * The one place a {@link WorkbookSpec} becomes a real `.xlsx`, and the one place the browser
 * is asked to save a file.
 *
 * **`exceljs` is loaded on the press, not on the page.** It is a large library and no TD is
 * carrying it around for the entire tournament in order to press Export twice: the dynamic
 * `import()` in {@link exportWorkbook} puts it in its own chunk, so the module strip, the
 * fixture list and every write path stay in a bundle that does not contain a spreadsheet
 * writer. The type-only import below is erased at compile time and pulls nothing in with it.
 *
 * **The styling is deliberately plain.** A banner row in the module's own colour, a bold header
 * band, a red tint on the cells that carry a rule breach, and column widths that make the sheet
 * readable when it is opened. Anything more would be decorating a working document that exists
 * to be filtered, sorted and printed by people who did not make it.
 */

import type { Workbook, Worksheet } from "exceljs";

import type { SheetSpec, WorkbookSpec } from "./workbook.js";

/**
 * The two module colours, as the pages already carry them: the site teal of a record, and the
 * `.aiband` orange of everything advisory (`styles/editorial.css`). The red is the alert wash
 * the rule-breach cells use on screen, so a violation looks the same in both places.
 */
const TEAL_FILL = "FFE8F2F2";
const TEAL_INK = "FF0A5457";
const ORANGE_FILL = "FFFBE7D2";
const ORANGE_INK = "FF8A4A12";
const ALERT_FILL = "FFFBECEA";
const ALERT_INK = "FFB3261E";

const MUTED_INK = "FF878E96";

function paint(sheet: Worksheet, row: number, columns: number, argb: string): void {
  for (let column = 1; column <= columns; column += 1) {
    sheet.getCell(row, column).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb },
    };
  }
}

function renderSheet(workbook: Workbook, spec: SheetSpec, exportedLine: string): void {
  const sheet = workbook.addWorksheet(spec.name);
  const width = spec.headers.length;
  const fill = spec.advisory ? ORANGE_FILL : TEAL_FILL;
  const ink = spec.advisory ? ORANGE_INK : TEAL_INK;

  sheet.getCell(1, 1).value = spec.title;
  sheet.getCell(1, 1).font = { bold: true, size: 13, color: { argb: ink } };
  sheet.getCell(2, 1).value = exportedLine;
  sheet.getCell(2, 1).font = { size: 10, color: { argb: MUTED_INK } };
  sheet.getCell(3, 1).value = spec.scope;
  sheet.getCell(3, 1).font = { size: 10, color: { argb: MUTED_INK } };

  let cursor = 4;
  if (spec.note !== null) {
    sheet.getCell(cursor, 1).value = spec.note;
    sheet.getCell(cursor, 1).font = { size: 10, italic: true, color: { argb: ink } };
    cursor += 1;
  }
  // The blank separator row the convention keeps, so the banner reads as a banner rather than
  // as four unlabelled data rows above the table.
  cursor += 1;

  const headerRow = cursor;
  spec.headers.forEach((header, index) => {
    const cell = sheet.getCell(headerRow, index + 1);
    cell.value = header;
    cell.font = { bold: true, color: { argb: ink } };
    cell.alignment = { vertical: "middle", wrapText: true };
  });
  paint(sheet, headerRow, width, fill);

  const violationColumns = new Set(
    spec.violationColumns
      .map((header) => spec.headers.indexOf(header) + 1)
      .filter((index) => index > 0),
  );
  const centredColumns = new Set(
    spec.centredColumns
      .map((header) => spec.headers.indexOf(header) + 1)
      .filter((index) => index > 0),
  );
  centredColumns.forEach((columnIndex) => {
    const cell = sheet.getCell(headerRow, columnIndex);
    cell.alignment = { vertical: "middle", wrapText: true, horizontal: "center" };
  });

  spec.rows.forEach((row, rowIndex) => {
    const target = headerRow + 1 + rowIndex;
    const bold = spec.boldRows.includes(rowIndex);
    row.forEach((value, columnIndex) => {
      const cell = sheet.getCell(target, columnIndex + 1);
      // `null` is written as an empty cell rather than as the string "null" or a dash — an
      // unfilled slot must stay unfilled when the sheet is sorted or counted.
      if (value !== null) cell.value = value;
      if (bold) cell.font = { bold: true };
      if (centredColumns.has(columnIndex + 1)) {
        cell.alignment = { horizontal: "center" };
      }
      if (violationColumns.has(columnIndex + 1) && value !== null && value !== "") {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ALERT_FILL } };
        cell.font = { bold: true, color: { argb: ALERT_INK } };
      }
    });
  });

  spec.widths.forEach((value, index) => {
    sheet.getColumn(index + 1).width = value;
  });

  // The banner scrolls away on a long fixture list; the column heads must not.
  sheet.views = [{ state: "frozen", ySplit: headerRow }];
}

/**
 * A spec, as a workbook. `create` supplies the constructor so this stays free of a runtime
 * `exceljs` import and a test can build one without a browser.
 */
export function renderWorkbook(spec: WorkbookSpec, create: () => Workbook): Workbook {
  const workbook = create();
  workbook.creator = "TD Control Centre";
  workbook.created = new Date();
  for (const sheet of spec.sheets) renderSheet(workbook, sheet, spec.exportedLine);
  return workbook;
}

/** The MIME type Excel and every sheet application recognise. */
const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Blob, object URL, anchor, click, revoke — the whole of the browser side, in one place so the
 * page containers never touch it and a page test can replace it with a spy.
 */
export async function downloadWorkbook(workbook: Workbook, filename: string): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buffer], { type: XLSX_MIME }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * The whole export, as one call: load the writer, render the spec, hand the file over.
 *
 * Both containers call this and neither knows how a workbook is written. It is also the seam
 * the page tests replace — a page test asserts the FILENAME and the SPEC it was given, and the
 * builder tests assert what that spec becomes, so nothing is proven twice and nothing is left
 * unproven between them.
 */
export async function exportWorkbook(spec: WorkbookSpec, filename: string): Promise<void> {
  const loaded = await import("exceljs");
  // The package ships a CommonJS build for Node and a UMD bundle for the browser, so which of
  // the two shapes an ESM `import()` hands back depends on which entry the bundler picked. Both
  // are accepted rather than one being assumed correct.
  const constructors = loaded as unknown as {
    Workbook?: new () => Workbook;
    default?: { Workbook: new () => Workbook };
  };
  const Ctor = constructors.Workbook ?? constructors.default?.Workbook;
  if (Ctor === undefined) throw new Error("The spreadsheet writer could not be loaded.");
  const workbook = renderWorkbook(spec, () => new Ctor());
  await downloadWorkbook(workbook, filename);
}

/** The seam both page containers hold — the real one above, a spy in a page test. */
export type WorkbookExporter = (spec: WorkbookSpec, filename: string) => Promise<void>;
